import { Button } from "@progress/kendo-react-buttons";
import { ListItemProps, TagData } from "@progress/kendo-react-dropdowns";
import { GridCellProps, GridCustomCellProps } from "@progress/kendo-react-grid";
import { Switch } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import {
  ArrowUpSquare,
  Ban,
  DownloadCloudIcon,
  Eye,
  EyeIcon,
  FileX,
  FileX2,
  Info,
  ListCheck,
  Lock,
  Mail,
  MoreHorizontal,
  PencilLine,
  ScrollText,
  Trash2
} from "lucide-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IShapeCodes } from "../interfaces/shapes.interface";
import { IBar, IBarSize, IStandard } from "../interfaces/standard.interface";
import bbsSvc from "../services/bbs.service";
import callOffSvc from "../services/callOff.service";
import companySvc from "../services/company.service";
import objectTypeSvc from "../services/objectType.service";
import templateMappingSvc from "../services/templateMapping.service";
import { setToast } from "../store/features/toastSlice";
import { useAppSelector } from "../store/hooks/hooks";
import { RootState, store } from "../store/store/store";
import {
  APPLICATION_TYPE_COLORS,
  STEPS_NAME_COLORS,
  STEPS_STATUS_COLORS,
} from "../utils/application.utils";
import { getDialogInfo } from "../utils/dialog.utils";
import {
  STATUS_CANCELLED,
  STATUS_COMPLETED,
  STATUS_EXPIRED,
  STATUS_NEW,
  STATUS_PENDING,
  STATUS_RENEWED,
  STATUS_STARTED,
} from "../utils/lease-status";
import { PROJECT_ADMIN, PROJECT_CONTRIBUTOR } from "../utils/roles.constant";
import { DATE_FORMAT, DATE_TIME_FORMAT, isAuthorize } from "../utils/utils";
import { CustomDialogBox } from "./dialog-box";

export const ActionCellPopupSettings = (props: any) => {
  const {
    rowData,
    handleEditClick,
    handleDeleteClick,
    dataKey,
    isStandard,
    action,
  } = props;
  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDialog = () => {
    setModalVisible(!modalVisible);
  };

  const handleDialog = (action: string) => {
    if (action === "yes") {
      handleDeleteClick(rowData);
    }

    toggleDialog();
  };

  const modalKey = isStandard ? "delete-standard" : "delete";

  return (
    <>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo(modalKey)}
          onToggleDeleteDialog={handleDialog}
        />
      )}

      <td className="text-end ">
        <div className="flex items-center gap-x-2 group">
          <div className="self-center cursor-pointer hover:underline hover:text-primaryMain">
            {rowData && dataKey && rowData[dataKey]}
          </div>
          {isAuthorize([PROJECT_ADMIN]) && <div className={`text-end`}>
            {/* <button className={`p-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 ${show ? 'active' : ''}`} */}
            {action !== "read" && (
              <button
                className={`px-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 `}
                onClick={onClick}
                ref={anchor}
              >
                <MoreHorizontal strokeWidth={1} />
              </button>
            )}
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px]"}
              anchorAlign={{ horizontal: "right", vertical: "top" }}
              popupAlign={{ horizontal: "left", vertical: "top" }}
            >
              <div ref={popupRef}>
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={() => {
                    setShow(false);
                    handleEditClick(rowData);
                  }}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <PencilLine className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">Edit</span>
                  </span>
                </Button>

                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={toggleDialog}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <Trash2 className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">Delete</span>
                  </span>
                </Button>
              </div>
            </Popup>
          </div>}
        </div>
      </td>
    </>
  );
};

export const ActionCellPopupStandards = (props: any) => {
  const { rowData, handleStandardAction, dataKey } = props;
  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDialog = () => {
    setModalVisible(!modalVisible);
  };

  const handleDialog = (action: string) => {
    if (action === "yes") {
      handleStandardAction("delete", rowData);
    }

    toggleDialog();
  };

  const modalKey = "delete-standard";

  return (
    <>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo(modalKey)}
          onToggleDeleteDialog={handleDialog}
        />
      )}

      <td className="text-end ">
        <div className="flex items-center gap-x-2 group">
          <div
            className="self-center cursor-pointer hover:underline hover:text-primaryMain"
            onClick={() => {
              setShow(false);
              handleStandardAction("read", rowData);
            }}
          >
            {rowData && dataKey && rowData[dataKey]}
          </div>
          {isAuthorize([PROJECT_ADMIN]) && <div className="text-end">
            {/* <button className={`p-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 ${show ? 'active' : ''}`} */}
            <button
              className={`p-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 `}
              onClick={onClick}
              ref={anchor}
            >
              <MoreHorizontal strokeWidth={1} />
            </button>
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px]"}
              anchorAlign={{ horizontal: "right", vertical: "top" }}
              popupAlign={{ horizontal: "left", vertical: "top" }}
            >
              <div ref={popupRef}>
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={() => {
                    setShow(false);
                    handleStandardAction("edit", rowData);
                  }}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <PencilLine className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">Edit</span>
                  </span>
                </Button>

                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={toggleDialog}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <Trash2 className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">Delete</span>
                  </span>
                </Button>
              </div>
            </Popup>
          </div>}
        </div>
      </td>
    </>
  );
};

export const RolesCell = (props: GridCustomCellProps) => {
  const label = props.dataItem.roles?.join(",");
  return <td {...props.tdProps}>{label}</td>;
};

export const CreatedOnDateCell = (props: GridCustomCellProps) => {
  const date = moment(props.dataItem.createdOn).format(DATE_TIME_FORMAT);

  return <td {...props.tdProps}>{date === "Invalid date" ? "" : date}</td>;
};

export const StartDateCell = (props: GridCustomCellProps) => {
  const date = moment(props.dataItem.StartDate).format(DATE_FORMAT);

  return <td {...props.tdProps}>{date}</td>;
};

export const EndDateCell = (props: GridCustomCellProps) => {
  const date = moment(props.dataItem.EndDate).format(DATE_FORMAT);

  return <td {...props.tdProps}>{date}</td>;
};

export const WeightCell = (props: GridCustomCellProps) => {
  return (
    <td
      className={`${props.dataItem.pasted ? "pasted-row" : ""} `}
      {...props.tdProps}
    >
      {props.dataItem.weight?.toFixed(3)}
    </td>
  );
};

export const TotalWeightCell = (props: GridCustomCellProps) => {
  return <td {...props.tdProps}>{props.dataItem.totalWeight?.toFixed(3)}</td>;
};

export const LeaseAmountCell = (props: GridCustomCellProps) => {
  return <td {...props.tdProps}>{props.dataItem.Amount} AED</td>;
};

export const LicenseExpiryDateCell = (props: GridCustomCellProps) => {
  const date = moment(props.dataItem.LicenseExpiryDate).format(DATE_FORMAT);

  return <td {...props.tdProps}>{date}</td>;
};

export const ChequeDateCell = (props: GridCustomCellProps) => {
  const chequeDate = moment
    .utc(props.dataItem.Date)
    .local()
    .format(DATE_FORMAT);

  return <td {...props.tdProps}>{chequeDate}</td>;
};

export const DateTimeCell = (props: GridCustomCellProps) => {
  let dateTime = "";

  if (props.dataItem.lastLoginDate) {
    dateTime = moment(props.dataItem.lastLoginDate).format(DATE_TIME_FORMAT);
  } else {
    dateTime = "---";
  }

  return <td {...props.tdProps}>{dateTime}</td>;
};

export const LeaseMonthCell = (props: GridCustomCellProps) => {
  const label = props.dataItem.LeaseMonths > 1 ? "Months" : "Month";

  return (
    <td {...props.tdProps}>
      {props.dataItem.LeaseMonths} {label}
    </td>
  );
};

export const StatusCell = (props: GridCustomCellProps) => {
  let color = "";
  switch (props.dataItem.Status) {
    case STATUS_EXPIRED:
      color = "bg-red-800 text-white";
      break;

    case STATUS_CANCELLED:
      color = "bg-red-600 text-white";
      break;

    case STATUS_NEW:
    case STATUS_RENEWED:
      color = "bg-primaryMain text-white";
      break;

    case STATUS_PENDING:
      color = "bg-warning-100 text-txtPrimary";
  }
  return (
    <td {...props.tdProps}>
      <span
        className={`${color}  uppercase p-[5px] text-[10px] font-bold rounded-[2px]`}
      >
        {props.dataItem.Status}
      </span>
    </td>
  );
};

export const CitiesDropdownItemRender = (li: any, itemProps: ListItemProps) => {
  const itemChildren = (
    <div>
      <div className="font-bold">{li.props.children}</div>
      <div className="text-xs">
        {itemProps.dataItem.CountryName} - {itemProps.dataItem.StateName}
      </div>
    </div>
  );

  return React.cloneElement(li, li.props, itemChildren);
};

export const EmployeesDropdownItemRender = (
  li: any,
  itemProps: ListItemProps
) => {
  const itemChildren = (
    <div>
      {itemProps.dataItem.Id} | {itemProps.dataItem.FullName}
    </div>
  );

  return React.cloneElement(li, li.props, itemChildren);
};

export const EmployeesMultiSelectTagRender = (tagData: TagData, li: any) => {
  const itemChildren = (
    <div>
      {tagData.data[0].Id} | {tagData.data[0].FullName}
    </div>
  );

  return React.cloneElement(li, li.props, itemChildren);
};

export const EmployeeStatusData = (props: GridCustomCellProps) => {
  const [status, setStatus] = React.useState<string>("");

  useEffect(() => {
    const status = props.dataItem.Status || props.dataItem.ApplicationStatus;
    setStatus(status);
  }, [props.dataItem]);

  return (
    <>
      <td>
        <div className="flex items-center gap-x-2 group">
          <div
            className="self-center text-[14px] p-[5px] rounded-sm px-3 text-black"
            style={{
              background:
                STEPS_NAME_COLORS[status as keyof typeof STEPS_NAME_COLORS],
            }}
          >
            {status}
          </div>
        </div>
      </td>
    </>
  );
};

export const ApplicationStatusData = (props: GridCustomCellProps) => {
  const [status, setStatus] = React.useState<string>("");

  useEffect(() => {
    const status = props.dataItem.Status || props.dataItem.ApplicationStatus;
    setStatus(status);
  }, [props.dataItem]);

  return (
    <>
      <td>
        <div className="flex items-center gap-x-2 group">
          <div
            className="self-center text-[14px] p-[5px] rounded-sm px-3 text-white"
            style={{
              background:
                STEPS_STATUS_COLORS[status as keyof typeof STEPS_STATUS_COLORS],
            }}
          >
            {status?.toUpperCase()}
          </div>
        </div>
      </td>
    </>
  );
};

export const ApplicationTypeData = (props: GridCustomCellProps) => {
  const [type, setType] = React.useState<string>("");

  useEffect(() => {
    const status =
      props.dataItem.ApplicationType || props.dataItem.ApplicationType;
    setType(status);
  }, [props.dataItem]);

  return (
    <>
      <td>
        <div className="flex items-center gap-x-2 group">
          <div
            className="self-center text-[14px] p-[5px] rounded-sm px-3 text-white"
            style={{
              color:
                APPLICATION_TYPE_COLORS[
                  type as keyof typeof APPLICATION_TYPE_COLORS
                ],
              border: `1px solid ${
                APPLICATION_TYPE_COLORS[
                  type as keyof typeof APPLICATION_TYPE_COLORS
                ]
              }`,
            }}
          >
            {type?.toUpperCase()}
          </div>
        </div>
      </td>
    </>
  );
};

export const CurrentStepData = (props: GridCustomCellProps) => {
  return (
    <>
      <td>
        {props.dataItem.IncompleteSteps?.split("|")
          .map((x: any) => x.split(":")[1])
          .join(", ") || ""}
      </td>
    </>
  );
};

export const ApplicationDateCell = (props: GridCustomCellProps) => {
  const date = moment(props.dataItem.ModifiedOn).format(DATE_FORMAT);

  return <td {...props.tdProps}>{date}</td>;
};

export const ApplicationListingActionCellPopup = (
  props: GridCustomCellProps
) => {
  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);
  const navigate = useNavigate();

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [cancelModalVisible, setCancelModalVisible] = React.useState(false);
  const [abscondModalVisible, setAbscondModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleCancelDialog = () => {
    setCancelModalVisible(!cancelModalVisible);
  };

  const handleCancelDialog = (action: string) => {
    if (action === "yes") {
      navigate(`/visa/application/-1`, {
        state: {
          action: "cancellation",
          employeeClass: props.dataItem.EmployeeClass,
          employeeId: props.dataItem.EmployeeId,
          createdOn: props.dataItem.CreatedOn,
        },
      });
    }

    toggleCancelDialog();
  };

  const toggleAbscondDialog = () => {
    setAbscondModalVisible(!abscondModalVisible);
  };

  const handleAbsondDialog = (action: string) => {
    if (action === "yes") {
      navigate(`/visa/application/-1`, {
        state: {
          action: "absconding",
          employeeClass: props.dataItem.EmployeeClass,
          employeeId: props.dataItem.EmployeeId,
          createdOn: props.dataItem.CreatedOn,
        },
      });
    }

    toggleAbscondDialog();
  };

  return (
    <>
      {cancelModalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("cancelApplication")}
          onToggleDeleteDialog={handleCancelDialog}
        />
      )}

      {abscondModalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("abscondApplication")}
          onToggleDeleteDialog={handleAbsondDialog}
        />
      )}

      <td className="text-end ">
        <div className="flex items-center gap-x-2 group">
          <div className="self-center">{props.dataItem.EmployeeName}</div>
          <div className="text-end">
            <button
              className={`p-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible group-hover:opacity-100 group-hover:visible  transition-all duration-300 ${
                show ? "active" : ""
              }`}
              onClick={onClick}
              ref={anchor}
            >
              <MoreHorizontal strokeWidth={1} />
            </button>
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px]"}
              anchorAlign={{ horizontal: "right", vertical: "bottom" }}
              popupAlign={{ horizontal: "right", vertical: "top" }}
            >
              <div ref={popupRef}>
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={() =>
                    navigate(`/visa/application/${props.dataItem.Id}`, {
                      state: {
                        action: "view",
                        employeeClass: props.dataItem.EmployeeClass,
                        employeeId: props.dataItem.EmployeeId,
                        createdOn: props.dataItem.CreatedOn,
                      },
                    })
                  }
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <Eye className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">View</span>
                  </span>
                </Button>

                {(props.dataItem.Status === STATUS_PENDING ||
                  props.dataItem.Status === STATUS_STARTED) && (
                  <Button
                    className="w-full iconBtn px-1"
                    fillMode="flat"
                    onClick={() =>
                      navigate(`/visa/application/${props.dataItem.Id}`, {
                        state: {
                          action: "edit",
                          employeeClass: props.dataItem.EmployeeClass,
                          employeeId: props.dataItem.EmployeeId,
                          createdOn: props.dataItem.CreatedOn,
                        },
                      })
                    }
                  >
                    <span className="inline-flex items-center gap-2 w-full">
                      <PencilLine className="w-4 h-4" />
                      <span className="flex-1 text-sm font-normal">Edit</span>
                    </span>
                  </Button>
                )}

                {props.dataItem.Status === STATUS_COMPLETED && (
                  <Button
                    className="w-full iconBtn px-1"
                    fillMode="flat"
                    onClick={toggleCancelDialog}
                  >
                    <span className="inline-flex items-center gap-2 w-full">
                      <FileX className="w-4 h-4" />
                      <span className="flex-1 text-sm font-normal">Cancel</span>
                    </span>
                  </Button>
                )}

                {props.dataItem.Status === STATUS_COMPLETED && (
                  <Button
                    className="w-full iconBtn px-1"
                    fillMode="flat"
                    onClick={toggleAbscondDialog}
                  >
                    <span className="inline-flex items-center gap-2 w-full">
                      <FileX className="w-4 h-4" />
                      <span className="flex-1 text-sm font-normal">
                        Abscond
                      </span>
                    </span>
                  </Button>
                )}
              </div>
            </Popup>
          </div>
        </div>
      </td>
    </>
  );
};

export const SubContractorActionCellPopup = (
  props: GridCustomCellProps & { handleEditClick: any; handleDeleteClick: any }
) => {
  const navigate = useNavigate();

  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDialog = () => {
    setModalVisible(!modalVisible);
  };

  const handleDialog = (action: string) => {
    if (action === "yes") {
      props.handleDeleteClick(props.dataItem);
    }

    toggleDialog();
  };

  return (
    <>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("delete")}
          onToggleDeleteDialog={handleDialog}
        />
      )}

      <td className="text-end ">
        <div className="flex items-center gap-x-2 group">
          <div className="self-center cursor-pointer hover:underline hover:text-primaryMain">
            {props.dataItem.name}
          </div>
          {isAuthorize([PROJECT_ADMIN]) && <div className="text-end">
            <button
              className={`p-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 ${
                show ? "active" : ""
              }`}
              onClick={onClick}
              ref={anchor}
            >
              <MoreHorizontal strokeWidth={1} />
            </button>
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px]"}
              anchorAlign={{ horizontal: "right", vertical: "top" }}
              popupAlign={{ horizontal: "left", vertical: "top" }}
            >
              <div ref={popupRef}>
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={() => props?.handleEditClick(props.dataItem)}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <PencilLine className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Edit</span>
                  </span>
                </Button>

                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={toggleDialog}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <Trash2 className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Delete</span>
                  </span>
                </Button>
              </div>
            </Popup>
          </div>}
        </div>
      </td>
    </>
  );
};

export const CompanyActionCellPopup = (
  props: GridCustomCellProps & {
    handleEditClick: any;
    handleEnableDisable: any;
    fetchCompanies: any;
  }
) => {
  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDelete = async (rowData: any) => {
    await companySvc.delete(rowData.id);
    props.fetchCompanies();
  };

  const toggleDialog = () => {
    setModalVisible(!modalVisible);
  };

  const handleDialog = (action: string) => {
    if (action === "yes") {
      handleDelete(props.dataItem);
      setShow(false);
    }

    toggleDialog();
  };

  const handleEnableDisable = async () => {
    if (props.dataItem.isActive) {
      await companySvc.update(props.dataItem.id, {
        ...props.dataItem,
        isActive: false,
      });
    } else {
      await companySvc.update(props.dataItem.id, {
        ...props.dataItem,
        isActive: true,
      });
    }
    props.fetchCompanies();
    setShow(false);
  };

  return (
    <>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("delete")}
          onToggleDeleteDialog={handleDialog}
        />
      )}

      <td className="text-end ">
        <div className="flex items-center gap-x-2 group">
          <div className="self-center cursor-pointer hover:underline hover:text-primaryMain">
            {props.dataItem.name}
          </div>
          {isAuthorize([PROJECT_ADMIN]) && <div className="text-end">
            <button
              className={`p-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 ${
                show ? "active" : ""
              }`}
              onClick={onClick}
              ref={anchor}
            >
              <MoreHorizontal strokeWidth={1} />
            </button>
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px]"}
              anchorAlign={{ horizontal: "right", vertical: "top" }}
              popupAlign={{ horizontal: "left", vertical: "top" }}
            >
              <div ref={popupRef}>
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={() => props?.handleEditClick(props.dataItem)}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <PencilLine className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Edit</span>
                  </span>
                </Button>

                {/* <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={handleEnableDisable}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    {props.dataItem.isActive === false && (
                      <ShieldCheck className="w-4 h-4" />
                    )}
                    {props.dataItem.isActive === true && (
                      <ShieldMinus className="w-4 h-4" />
                    )}
                    <span className="flex-1 text-sm font-normal">

                      {props.dataItem.isActive ? "Disable" : "Enable"}
                    </span>
                  </span>
                </Button> */}
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={toggleDialog}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <Trash2 className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Delete</span>
                  </span>
                </Button>
              </div>
            </Popup>
          </div>}
        </div>
      </td>
    </>
  );
};

export const ObjectTypeActionCellPopup = (
  props: GridCustomCellProps & { handleEditClick: any; fetchObjectTypes: any }
) => {
  const navigate = useNavigate();

  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDelete = async (rowData: any) => {
    await objectTypeSvc.delete(rowData.id);
    props.fetchObjectTypes();
    // navigate("/settings/object-types");
  };

  const toggleDialog = () => {
    setModalVisible(!modalVisible);
  };

  const handleDialog = (action: string) => {
    if (action === "yes") {
      handleDelete(props.dataItem);
    }

    toggleDialog();
  };

  return (
    <>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("delete")}
          onToggleDeleteDialog={handleDialog}
        />
      )}

      <td className="text-end ">
        <div className="flex items-center gap-x-2 group">
          <div className="self-center cursor-pointer hover:underline hover:text-primaryMain">
            {props.dataItem.name}
          </div>
          {isAuthorize([PROJECT_ADMIN]) && <div className="text-end">
            <button
              className={`p-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 ${
                show ? "active" : ""
              }`}
              onClick={onClick}
              ref={anchor}
            >
              <MoreHorizontal strokeWidth={1} />
            </button>
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px]"}
              anchorAlign={{ horizontal: "right", vertical: "top" }}
              popupAlign={{ horizontal: "left", vertical: "top" }}
            >
              <div ref={popupRef}>
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={() => props?.handleEditClick(props.dataItem)}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <PencilLine className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Edit</span>
                  </span>
                </Button>

                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={toggleDialog}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <Trash2 className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Delete</span>
                  </span>
                </Button>
              </div>
            </Popup>
          </div>}
        </div>
      </td>
    </>
  );
};

export const MappingEnabledCell = (
  props: GridCellProps,
  handleEnableDisable: any
) => {
  const { dataItem, field } = props;

  return (
    <td>
      <Switch
        checked={props.dataItem.isActive}
        onLabel="Yes"
        offLabel="No"
        name="isActive"
        value={dataItem.isActive}
        onChange={(e) => handleEnableDisable(props.dataItem)}
      ></Switch>
    </td>
  );
};

export const MappingActionCellPopup = (
  props: GridCustomCellProps & { fetchMappings: any }
) => {
  const navigate = useNavigate();

  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDelete = async (rowData: any) => {
    await templateMappingSvc.delete(rowData.id);
    props.fetchMappings();
  };

  const handleDisable = async () => {
    await templateMappingSvc.update(props.dataItem.id, {
      ...props.dataItem,
      isEnabled: false,
    });
    props.fetchMappings();
    // navigate('/settings/mappings');
  };

  const handleEnable = async (rowData: any) => {
    await templateMappingSvc.update(props.dataItem.id, {
      ...props.dataItem,
      isEnabled: true,
    });
    props.fetchMappings();
    // navigate('/settings/mappings');
  };

  const toggleDialog = () => {
    setModalVisible(!modalVisible);
  };

  const handleDialog = (action: string) => {
    if (action === "yes") {
      handleDelete(props.dataItem);
    }

    toggleDialog();
  };

  return (
    <>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("delete")}
          onToggleDeleteDialog={handleDialog}
        />
      )}

      <td className="text-end ">
        <div className="flex items-center gap-x-2 group">
          <div
            className="self-center cursor-pointer hover:underline hover:text-primaryMain"
            onClick={() =>
              navigate(
                `/settings/create-mapping?action=view&id=${props.dataItem.id}`
              )
            }
          >
            {props.dataItem.mappingName}
          </div>
          {isAuthorize([PROJECT_ADMIN]) && <div className="text-end">
            <button
              className={`p-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 ${
                show ? "active" : ""
              }`}
              onClick={onClick}
              ref={anchor}
            >
              <MoreHorizontal strokeWidth={1} />
            </button>
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px]"}
              anchorAlign={{ horizontal: "right", vertical: "top" }}
              popupAlign={{ horizontal: "left", vertical: "top" }}
            >
              <div ref={popupRef}>
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={() =>
                    navigate(
                      `/settings/create-mapping?action=edit&id=${props.dataItem.id}`
                    )
                  }
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <PencilLine className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Edit</span>
                  </span>
                </Button>
                {/* {props.dataItem.isEnabled && (
                  <Button
                    className="w-full iconBtn px-1"
                    fillMode="flat"
                    onClick={handleDisable}
                  >
                    <span className="inline-flex items-center gap-2 w-full">
                      <FileX className="w-4 h-4" />
                      <span className="flex-1 text-sm font-normal">

                        Disable
                      </span>
                    </span>
                  </Button>
                )}
                {!props.dataItem.isEnabled && (
                  <Button
                    className="w-full iconBtn px-1"
                    fillMode="flat"
                    onClick={handleEnable}
                  >
                    <span className="inline-flex items-center gap-2 w-full">
                      <PencilLine className="w-4 h-4" />
                      <span className="flex-1 text-sm font-normal">

                        Enable
                      </span>
                    </span>
                  </Button>
                )} */}
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={toggleDialog}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <Trash2 className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Delete</span>
                  </span>
                </Button>
              </div>
            </Popup>
          </div>}
        </div>
      </td>
    </>
  );
};

export const BBSImportCell = (
  props: GridCustomCellProps & {
    updatedFields: Record<string, string[]>;
    shapeCodes: IShapeCodes[];
    isInvalid: boolean;
    setIsInvalid: React.Dispatch<React.SetStateAction<boolean>>;
    errsList: any;
    setErrsList: React.Dispatch<any>;
    invalidMembers: string[];
    invalidMarks: any[];
    standard: IStandard | null;
  }
) => {
  const dimensions = [
    "dimA",
    "dimB",
    "dimC",
    "dimD",
    "dimE",
    "dimF",
    "dimG",
    "dimH",
    "dimJ",
    "dimK",
    "dimO",
    "dimR",
    "dimI",
    "dimH1",
    "dimH2",
  ];

  const commonState = useAppSelector((state: RootState) => state.common);
  const barsSizes: IBarSize[] = props.standard?.barSizes!;
  const bars: IBar[] = props.standard?.bars!;
  const [isInvalidCell, setIsInvalidCell] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  useEffect(() => {
    if (props?.dataItem) {
      const key = `${props.dataItem.name}:${props.dataItem.mark}:${props.dataItem.isActive}`;
      let isInvalidTemp = false;
      // Validate Bar Size and Bar Types
      const barSizeObj = barsSizes.find(
        (x) => x.size === Number(props.dataItem.size)
      );
      const bar = bars.find((x) => x.typeCode === props.dataItem.type);
      if (props.dataItem.name && props.dataItem.mark) {
        if (key in props.updatedFields) {
          if (
            props.updatedFields[key] &&
            props.field &&
            props.updatedFields[key].includes(props.field)
          ) {
            setIsUpdated(true);
          }
        }
      }

      switch (props.field) {
        case "shapeCode":
          if (!props?.shapeCodes?.length) {
            isInvalidTemp = false;
          } else {
            isInvalidTemp = !props.shapeCodes.some(
              (shapeCode) => shapeCode.code == props.dataItem[props.field!]
            );
          }
          break;

        case "noOfMem":
          if (props.invalidMembers.includes(props.dataItem.name)) {
            isInvalidTemp = true;
          }
          break;
        case "type":
          // Validate Bar Size and Bar Types
          if (!bar) {
            isInvalidTemp = true;
          }
          break;
        case "size":
          if (!barSizeObj) {
            isInvalidTemp = true;
          }
          break;

        case "mark":
          if (
            props.invalidMarks.find(
              (x) =>
                x.memberName == props.dataItem.name &&
                x.value == props.dataItem.mark
            )
          ) {
            isInvalidTemp = true;
          }
          break;
        case "length":
          if (props.dataItem.isInvalidBarLength === true) {
            isInvalidTemp = true;
            if (!(key in props.errsList)) {
            }

            break;
          }
          if (commonState?.selectedProject?.event?.item?.barLength) {
            isInvalidTemp =
              props.dataItem[props.field] >
              commonState.selectedProject.event.item.barLength;
            if (!(key in props.errsList) && isInvalidTemp === true) {
            }
            break;
          } else {
            isInvalidTemp = false;
            break;
          }

        default:
          if (dimensions.includes(props.field || "")) {
            const shapeCode = props.dataItem.shapeCode;
            const shape = props.shapeCodes.find((x) => x.code === shapeCode);

            if (
              shape &&
              shape[props.field! as keyof IShapeCodes] === false &&
              props.dataItem[props.field!] !== 0
            ) {
              isInvalidTemp = true;
              break;
            } else if (
              shape &&
              shape[props.field! as keyof IShapeCodes] === true &&
              !props.dataItem[props.field!]
            ) {
              isInvalidTemp = true;
              break;
            }
          }

          isInvalidTemp = false;
          setIsInvalidCell(false);
          break;
      }
      setIsInvalidCell(isInvalidTemp);
      if (props.isInvalid === false && isInvalidTemp === true) {
        props.setIsInvalid(isInvalidTemp);
      }
    }
  }, [props]);

  const getClass = () => {
    if (isInvalidCell) {
      return "text-[#A4262C] bg-[#F3D2D4]";
    }

    if (props.field && props.dataItem[props.field] && isUpdated) {
      return "text-[#FFE58D] bg-[#B58F0E]";
    }

    return "";
  };

  return (
    <>
      <td className={getClass()}>
        {props.field == "weight" &&
          props.dataItem[props.field] &&
          props.dataItem[props.field].toFixed(3)}
        {props.field && props.field != "weight" && props.dataItem[props.field]}
      </td>
    </>
  );
};

export const StatusCellA = (props: GridCellProps) => {
  const { dataItem, field } = props;
  const status = dataItem[field || ""];

  const getStatusClass = (status: string) => {
    switch (status) {
      case "Added":
        return "text-[#063206] bg-[#DFF6DD]";
      case "Updated":
      case "No Change":
        return "text-[#FFE58D] bg-[#B58F0E]";
      case "Deleted":
        return "text-[#A4262C] bg-[#F3D2D4]";
      case "Revised":
        return "text-[#4094DC] bg-[#CEE5FF]";
      default:
        return "";
    }
  };

  return (
    <td>
      <span className={`px-2 py-1 rounded ${getStatusClass(status)}`}>
        {status}
      </span>
    </td>
  );
};

export const MemberNameCell: React.FC<
  GridCellProps & {
    callOffMembers: string[];
    shapeCodes: IShapeCodes[];
    invalidMembers: string[];
    invalidMarks: any[];
    invalidShapesMembers: string[];
  }
> = (props) => {
  const { dataItem, field } = props;
  const name = dataItem[field || ""];
  const commonState = useAppSelector((state: RootState) => state.common);

  const [isInvalid, setIsInvalid] = useState(false);

  const isDimensionMissing = (shapeCode: IShapeCodes) => {
    const dimensions = [
      "dimA",
      "dimB",
      "dimC",
      "dimD",
      "dimE",
      "dimF",
      "dimG",
      "dimH",
      "dimJ",
      "dimK",
      "dimO",
      "dimR",
      "dimI",
      "dimH2",
    ];

    for (const dimension of dimensions) {
      if (
        shapeCode[dimension as keyof IShapeCodes] &&
        !props.dataItem[dimension]
      ) {
        return true;
      }
    }
  };

  useEffect(() => {
    if (props.dataItem) {
      if (!props.dataItem.name) {
        setIsInvalid(true);
        return;
      }
      if (!props?.shapeCodes?.length) {
        setIsInvalid(true);
      } else {
        const isInvalidTemp = !props.shapeCodes.some(
          (shapeCode) => shapeCode.code == props.dataItem.shapeCode
        );

        if (!isInvalidTemp) {
          const res = isDimensionMissing(
            props.shapeCodes.find((x) => x.code === props.dataItem.shapeCode)!
          );
          if (res) {
            setIsInvalid(true);
            return;
          }
        }
        setIsInvalid(isInvalidTemp);
      }

      if (props.invalidMembers.includes(props.dataItem.name)) {
        setIsInvalid(true);
      }
      for (let i = 0; i < props.invalidShapesMembers.length; i++) {
        const parts = props.invalidShapesMembers[i].split("$$");
        if (
          parts[0] === props.dataItem.name &&
          parts[1] === props.dataItem.shapeCode &&
          parts[3] === props.dataItem.mark
        ) {
          setIsInvalid(true);
        }
      }

      if (
        props.dataItem.length > commonState.selectedProject.event.item.barLength
      ) {
        setIsInvalid(true);
      }

      if (props.invalidMarks.find((x) => x.memberName == props.dataItem.name)) {
        setIsInvalid(true);
      }

      // if(props.callOffMembers.find((x) => x === props.dataItem.name)){
      //   setIsInvalid(true);
      // }
    }
  }, [props]);

  return (
    <td className="p-2">
      <div className="flex items-center gap-2">
        <p className="flex-1">{name}</p>
        {isInvalid && (
          <span className=" text-[#A4262C] border-[#A4262C] border rounded-sm inline-flex items-center justify-center p-[5px_5px]">
            <Info strokeWidth={1} size={14} />
          </span>
        )}
      </div>
    </td>
  );
};

export const DateCell = (props: GridCustomCellProps) => {
  const { dataItem, field } = props;

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const formattedDate = moment(dateString).format(DATE_TIME_FORMAT);
    return formattedDate;
  };

  return (
    <td {...props.tdProps}>
      <span className={`px-2 py-1 rounded`}>
        {formatDate(dataItem[field || ""])}
      </span>
    </td>
  );
};

export const BBSActionCellPopup = (
  props: GridCustomCellProps & {
    handleEditClick: any;
    handleImport: any;
    handleExport: any;
    handleDelete: any;
    handleBBSLogsClick: any;
  }
) => {
  const navigate = useNavigate();

  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDelete = async (rowData: any) => {
    try {
      const response = await bbsSvc.delete(rowData.id);

      if (!response.error) {
        // Success
        store.dispatch(
          setToast({
            toastType: "success",
            toastMsg:
              (response.data as any)?.message || "BBS deleted successfully.",
          })
        );
        props.handleDelete();
      } else {
        // Handle error based on status code and error code
        if (
          response.status === 400 &&
          response.errorCode === "BBS_HAS_CALLOFFS"
        ) {
          store.dispatch(
            setToast({
              toastType: "error",
              toastMsg:
                "Cannot delete BBS. Call-Off records exist for this BBS.",
            })
          );
        } else if (response.status === 404) {
          store.dispatch(
            setToast({
              toastType: "error",
              toastMsg: "BBS not found.",
            })
          );
        } else {
          store.dispatch(
            setToast({
              toastType: "error",
              toastMsg:
                (response.error as any)?.message || "Failed to delete BBS.",
            })
          );
        }
      }
    } catch (error) {
      console.error("Error deleting BBS:", error);
      store.dispatch(
        setToast({
          toastType: "error",
          toastMsg: "An unexpected error occurred while deleting the BBS.",
        })
      );
    }
  };

  const toggleDialog = () => {
    setModalVisible(!modalVisible);
  };

  const handleDialog = (action: string) => {
    if (action === "yes") {
      handleDelete(props.dataItem);
    }

    toggleDialog();
  };

  return (
    <>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("delete")}
          onToggleDeleteDialog={handleDialog}
        />
      )}

      <td className="text-end " {...props.tdProps}>
        <div className="flex items-center gap-x-2 group">
          <div className="self-center cursor-pointer hover:underline hover:text-primaryMain">
            {props.dataItem.no}
          </div>
          <div className="text-end">
            <button
              className={`px-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 ${
                show ? "active" : ""
              }`}
              onClick={onClick}
              ref={anchor}
            >
              <MoreHorizontal strokeWidth={1} />
            </button>
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px] bbsEditPopup"}
              anchorAlign={{ horizontal: "right", vertical: "top" }}
              popupAlign={{ horizontal: "left", vertical: "top" }}
            >
              <div ref={popupRef}>
                {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && (
                  <Button
                    className="w-full iconBtn px-1"
                    fillMode="flat"
                    onClick={() => {
                      setShow(!show);
                      props?.handleEditClick(props.dataItem);
                  }}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <PencilLine className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal"> Edit</span>
                  </span>
                </Button>)}

                {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && (
                  <Button
                    className="w-full iconBtn px-1"
                    fillMode="flat"
                    onClick={() => {
                      setShow(!show);
                      props?.handleImport(props.dataItem);
                    }}
                  >
                    <span className="inline-flex items-center gap-2 w-full">
                      <ArrowUpSquare className="w-4 h-4" />
                      <span className="flex-1 text-sm font-normal">
                        Import BBS
                      </span>
                    </span>
                  </Button>
                )}

                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  onClick={() => {
                    setShow(!show);
                    props?.handleExport(props.dataItem);
                  }}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <FileX2 className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">
                      Export BBS
                    </span>
                  </span>
                </Button>

                {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && (
                  <Button
                    className="w-full iconBtn px-1"
                    fillMode="flat"
                    onClick={toggleDialog}
                  >
                    <span className="inline-flex items-center gap-2 w-full">
                      <Trash2 className="w-4 h-4" />
                      <span className="flex-1 text-sm font-normal"> Delete</span>
                    </span>
                  </Button>)}
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  size={"small"}
                  onClick={() => {
                    setShow(!show);
                    props?.handleBBSLogsClick(props.dataItem);
                  }}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <ScrollText className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">Logs</span>
                  </span>
                </Button>
              </div>
            </Popup>
          </div>
        </div>
      </td>
    </>
  );
};

interface ImageCellProps {
  imagePath: string;
  code: string;
  onPreview: (path: string, code: string) => void;
  onDownload: (path: string, code: string) => void;
}
const processBase64 = (input: string): string => {
  if (!input) return "";

  try {
    let base64 = input;
    let mimeType = "image/png";

    // Remove data URI prefix if present
    const prefixRegex = /^data:(image\/\w+);base64,/i;
    const match = base64.match(prefixRegex);
    if (match) {
      mimeType = match[1];
      base64 = base64.replace(prefixRegex, "");
    }

    // Clean invalid characters and whitespace
    base64 = base64.replace(/[^A-Za-z0-9+/=]/g, "").trim();

    // Add padding if needed
    while (base64.length % 4 !== 0) {
      base64 += "=";
    }

    // Validate with atob
    try {
      atob(base64);
    } catch (e) {
      console.error("Invalid Base64 after cleaning:", e);
      return "";
    }

    return `data:${mimeType};base64,${base64}`;
  } catch (e) {
    console.error("Base64 processing error:", e);
    return "";
  }
};
export const ImageCell: React.FC<ImageCellProps> = ({
  imagePath,
  code,
  onPreview,
  onDownload,
}) => {
  const [processedUrl, setProcessedUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!imagePath) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    try {
      console.log("Received Base64 from backend:", imagePath);
      const url = processBase64(imagePath);
      console.log("Processed Base64 URL:", url);
      setProcessedUrl(url);
      setHasError(!url);
    } catch (e) {
      console.error("Error processing image path:", e);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [imagePath]);

  if (isLoading) {
    return (
      <td>
        <div className="flex justify-center w-full h-full">
          <p>Loading...</p>
        </div>
      </td>
    );
  }

  if (hasError || !processedUrl) {
    return (
      <td>
        <div className="flex justify-center w-full h-full">
          <p>No Image</p>
        </div>
      </td>
    );
  }

  return (
    <td>
      <div className="relative group transition-all duration-500 flex items-center justify-center flex-col border-neutral-30 bg-neutral-10 cursor-pointer w-[100px] overflow-hidden">
        <img
          src={processedUrl}
          width={100}
          height={100}
          className="object-contain"
          onError={(e) => {
            console.error(`Image load error for code ${code}:`, e);
            setHasError(true);
          }}
          alt={`Shape ${code}`}
        />
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
          <Button
            className="rounded p-2 text-white border border-white"
            onClick={() => onPreview(processedUrl, code)}
          >
            <EyeIcon size={16} className="w-4 h-4" />
          </Button>
          <Button
            className="rounded p-2 text-white border border-white"
            onClick={() => onDownload(processedUrl, code)}
          >
            <DownloadCloudIcon size={16} className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </td>
  );
};

export const CallOffActionCellPopup = (
  props: GridCustomCellProps & {
    handleEditClick: any;
    handleManageMemberClick: any;
    handleManageNoOfMemberClick: any;
    handleCancelCallOffClick: any;
    handleCallOffFabEmailClick: any;
    handleCallOffLogsClick: any;
    handleExport: any;
    handleDelete: any;
  }
) => {
  const navigate = useNavigate();

  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = React.useState(false);

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDelete = async (rowData: any) => {
    await callOffSvc.delete(rowData.id).then(async (x) => {
      props.handleDelete();
    });
    //navigate("/home");
  };

  const toggleDialog = () => {
    setModalVisible(!modalVisible);
  };

  const handleDialog = (action: string) => {
    if (action === "yes") {
      handleDelete(props.dataItem);
    }

    toggleDialog();
  };

  return (
    <>
      {modalVisible && (
        <CustomDialogBox
          dialogInfo={getDialogInfo("delete")}
          onToggleDeleteDialog={handleDialog}
        />
      )}

      <td className="text-end " {...props.tdProps}>
        <div className="flex items-center gap-x-2 group">
          <div className="self-center inline-flex gap-2 imte cursor-pointer hover:underline hover:text-primaryMain">
            {props.dataItem.status === "Released" && (
              <Lock className="w-4 h-4" />
            )}
            {props.dataItem.status === "Engineered" && (
              <ListCheck className="w-4 h-4" />
            )}
            <p className="text-wrap flex-1">{props.dataItem.name}</p>
          </div>
          <div className="text-end">
            <button
              className={`px-2 [&.active]:opacity-100 [&.active]:visible opacity-0 invisible cta_dropdown  transition-all duration-300 ${
                show ? "active" : ""
              }`}
              onClick={onClick}
              ref={anchor}
            >
              <MoreHorizontal strokeWidth={1} />
            </button>
            <Popup
              anchor={anchor.current}
              show={show}
              popupClass={"w-[161px] bbsEditPopup"}
              anchorAlign={{ horizontal: "right", vertical: "top" }}
              popupAlign={{ horizontal: "left", vertical: "top" }}
            >
              <div ref={popupRef}>
                {props.dataItem.status == "Engineered" && (
                  <>
                    {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                      className="w-full iconBtn px-1"
                      fillMode="flat"
                      size={"small"}
                      onClick={() => {
                        setShow(!show);
                        props?.handleEditClick(props.dataItem);
                      }}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <PencilLine className="w-4 h-4" />
                        <span className="flex-1 text-sm font-normal">Edit</span>
                      </span>
                    </Button>}
                    {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                      className="w-full iconBtn px-1"
                      fillMode="flat"
                      size={"small"}
                      onClick={() => {
                        setShow(!show);
                        props?.handleManageMemberClick(props.dataItem);
                      }}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <ListCheck className="w-4 h-4" />
                        <span className="flex-1 text-sm font-normal">
                          Manage Members
                        </span>
                      </span>
                    </Button>}

                    {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                      className="w-full iconBtn px-1"
                      fillMode="flat"
                      size={"small"}
                      onClick={() => {
                        setShow(!show);
                        props?.handleManageNoOfMemberClick(props.dataItem);
                      }}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <PencilLine className="w-4 h-4" />
                        <span className="flex-1 text-sm font-normal">
                          Edit No Of Members
                        </span>
                      </span>
                    </Button>}
                    {isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && <Button
                      className="w-full iconBtn px-1"
                      size={"small"}
                      fillMode="flat"
                      onClick={toggleDialog}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <Trash2 className="w-4 h-4" />
                        <span className="flex-1 text-sm font-normal">
                          Delete
                        </span>
                      </span>
                    </Button>}
                  </>
                )}
                {props.dataItem.sendToFab && isAuthorize([PROJECT_ADMIN, PROJECT_CONTRIBUTOR]) && (
                  <>
                    <Button
                      className="w-full iconBtn px-1"
                      fillMode="flat"
                      size={"small"}
                      onClick={() => {
                        setShow(!show);
                        props?.handleCancelCallOffClick(props.dataItem);
                      }}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <Ban className="w-4 h-4" />
                        <span className="flex-1 text-sm font-normal">
                          Cancel Call-Off
                        </span>
                      </span>
                    </Button>
                    <Button
                      className="w-full iconBtn px-1"
                      fillMode="flat"
                      size={"small"}
                      onClick={() => {
                        setShow(!show);
                        props?.handleCallOffFabEmailClick(props.dataItem);
                      }}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <Mail className="w-4 h-4" />
                        <span className="flex-1 text-sm font-normal">
                          Email to Fabricator
                        </span>
                      </span>
                    </Button>
                  </>
                )}
                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  size={"small"}
                  onClick={() => {
                    setShow(!show);
                    props?.handleExport(props.dataItem);
                  }}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <FileX2 className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">
                      Export Call-Off
                    </span>
                  </span>
                </Button>

                <Button
                  className="w-full iconBtn px-1"
                  fillMode="flat"
                  size={"small"}
                  onClick={() => {
                    setShow(!show);
                    props?.handleCallOffLogsClick(props.dataItem);
                  }}
                >
                  <span className="inline-flex items-center gap-2 w-full">
                    <ScrollText className="w-4 h-4" />
                    <span className="flex-1 text-sm font-normal">Logs</span>
                  </span>
                </Button>
              </div>
            </Popup>
          </div>
        </div>
      </td>
    </>
  );
};
